import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import EditSeasonMutation from "./mutations/EditSeason";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import {Divider} from "@mui/material";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

function cleanupBootstrapModal() {
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    document.querySelectorAll(".modal.show").forEach((el) => el.classList.remove("show"));
}

export default function EditSeasonModal({
                                                                                    showModal,
                                                                                    setShowModal,
                                                                                    seasonToEdit,
                                                                                    waivers,
                                                                                    division,
                                                                                }) {
    const [editSeason] = EditSeasonMutation();
    const colors = useThemeColors();
    const { t } = useTranslation();

    const formRef = useRef(null);

    const [useOvertime, setUseOvertime] = useState(false);
    const [selectedWaiverIds, setSelectedWaiverIds] = useState([]);
    const selectedWaiverTitles = useMemo(() => {
        const selectedSet = new Set((selectedWaiverIds || []).map((id) => String(id)));
        return (waivers || [])
            .filter((waiver) => selectedSet.has(String(waiver.id)))
            .map((waiver) => waiver.title);
    }, [selectedWaiverIds, waivers]);

    const [registrationFeeEnabled, setRegistrationFeeEnabled] = useState(false);
    const [registrationFeeType, setRegistrationFeeType] = useState("mandatory_on_registration");
    const [lateRegistrationFee, setLateRegistrationFee] = useState("");
    const [registrationFeeAmount, setRegistrationFeeAmount] = useState("");
    const [registrationFeeDueDate, setRegistrationFeeDueDate] = useState("");
    const [registrationFeeInstallments, setRegistrationFeeInstallments] = useState([
        { amount: "", dueDate: "" },
    ]);

    const ui = useMemo(() => {
        return {
            overlay: {
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                zIndex: 1050,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
            },
            dialog: {
                width: "100%",
                maxWidth: 1100,
            },
            card: {
                borderRadius: 18,
                overflow: "hidden",
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 24px 70px rgba(0,0,0,0.25)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: colors.textOnPrimary,
                padding: 0,
                lineHeight: 1,
            },
            body: { padding: 14, maxHeight: "72vh", overflow: "auto" },
            section: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            sectionTitleRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
            },
            sectionTitle: {
                margin: 0,
                fontSize: 13,
                fontWeight: 900,
                color: "rgba(20,21,26,0.85)",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
            },
            hint: { fontSize: 12.5, fontWeight: 700, color: "rgba(20,21,26,0.55)" },
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                // border: "1px solid rgba(0,0,0,0.10)",
                background: colors.white,
                color: colors.woodsmoke,
                fontWeight: 900,
                fontSize: 12,
            },
            footer: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: 14,
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            btnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },
            label: { fontWeight: 900, fontSize: 12.5, color: "rgba(20,21,26,0.75)" },
            labelFlagged: {
                fontSize: 12.5,
                fontWeight: 800,
                color: colors.danger,
                marginBottom: 6,
            },
            input: { borderRadius: 12, fontWeight: 700, border: "1px solid rgba(0,0,0,0.10)" },
            select: { borderRadius: 12, fontWeight: 700, border: "1px solid rgba(0,0,0,0.10)" },
            divider: { height: 1, background: "rgba(0,0,0,0.06)", margin: "10px 0" },
            inlineRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
        };
    }, [colors.primary, colors.textOnPrimary, colors.white, colors.woodsmoke, colors.danger]);

    const closeModal = () => {
        setShowModal(false);
        formRef.current?.reset();

        setUseOvertime(false);

        setRegistrationFeeEnabled(false);
        setRegistrationFeeType("mandatory_on_registration");
        setLateRegistrationFee("");
        setRegistrationFeeAmount("");
        setRegistrationFeeDueDate("");
        setRegistrationFeeInstallments([{ amount: "", dueDate: "" }]);
        setSelectedWaiverIds([]);

        cleanupBootstrapModal();
    };

    useEffect(() => {
        if (showModal) {
            document.body.classList.add("modal-open");
            document.body.style.overflow = "hidden";
        } else {
            cleanupBootstrapModal();
        }
        return () => cleanupBootstrapModal();
    }, [showModal]);

    useEffect(() => {
        if (!showModal || !seasonToEdit || !formRef.current) return;
        const form = formRef.current;

        form.elements.namedItem("id").value = seasonToEdit.id;
        form.elements.namedItem("name").value = seasonToEdit.name || "";
        form.elements.namedItem("registration_start_date").value =
            seasonToEdit.registrationStartDate?.substring(0, 10) || "";
        form.elements.namedItem("registration_end_date").value =
            seasonToEdit.registrationEndDate?.substring(0, 10) || "";
        form.elements.namedItem("late_registration_until").value =
            seasonToEdit.lateRegistrationUntil?.substring(0, 10) || "";
        form.elements.namedItem("start_date").value = seasonToEdit.startDate?.substring(0, 10) || "";
        form.elements.namedItem("end_date").value = seasonToEdit.endDate?.substring(0, 10) || "";
        setLateRegistrationFee(
            seasonToEdit?.lateRegistrationFee !== undefined && seasonToEdit?.lateRegistrationFee !== null
                ? String(seasonToEdit.lateRegistrationFee)
                : ""
        );
        setSelectedWaiverIds(
            seasonToEdit?.waivers?.map((w) => w.id) ||
            (seasonToEdit?.waiver?.id ? [seasonToEdit.waiver.id] : [])
        );

        if (!["baseball", "softball"].includes(division?.sport?.key)) {
            form.elements.namedItem("win_points").value = seasonToEdit.pointSystem?.winPoints ?? 0;
            form.elements.namedItem("draw_points").value = seasonToEdit.pointSystem?.drawPoints ?? 0;
            form.elements.namedItem("lose_points").value = seasonToEdit.pointSystem?.losePoints ?? 0;
            form.elements.namedItem("forfeit_win_points").value = seasonToEdit.pointSystem?.forfeitWinPoints ?? 3;
            form.elements.namedItem("forfeit_loss_points").value = seasonToEdit.pointSystem?.forfeitLossPoints ?? 0;
            form.elements.namedItem("forfeit_win_score").value = seasonToEdit.pointSystem?.forfeitWinScore ?? 3;
            form.elements.namedItem("forfeit_loss_score").value = seasonToEdit.pointSystem?.forfeitLossScore ?? 0;
            form.elements.namedItem("double_forfeit_points").value = seasonToEdit.pointSystem?.doubleForfeitPoints ?? 0;

            const overtime = seasonToEdit.pointSystem?.useOvertime ?? false;
            form.elements.namedItem("use_overtime").checked = overtime;
            setUseOvertime(overtime);

            if (overtime) {
                setTimeout(() => {
                    const otWin = form.elements.namedItem("ot_win_points");
                    const otLoss = form.elements.namedItem("ot_loss_points");
                    if (otWin) otWin.value = seasonToEdit.pointSystem?.otWinPoints ?? 0;
                    if (otLoss) otLoss.value = seasonToEdit.pointSystem?.otLossPoints ?? 0;
                }, 0);
            }
        }

        const feeType = (seasonToEdit.registrationFeeType || "mandatory_on_registration").toLowerCase();
        setRegistrationFeeType(feeType);

        if (seasonToEdit.registrationInstallments?.length > 0) {
            setRegistrationFeeEnabled(true);

            if (feeType === "installments") {
                setRegistrationFeeInstallments(
                    seasonToEdit.registrationInstallments.map((inst) => ({
                        amount: inst.amount ?? "",
                        dueDate: inst.dueDate?.substring(0, 10) ?? "",
                    }))
                );
                setRegistrationFeeAmount("");
                setRegistrationFeeDueDate("");
            } else {
                setRegistrationFeeAmount(seasonToEdit.registrationInstallments[0]?.amount ?? "");
                setRegistrationFeeDueDate(seasonToEdit.registrationInstallments[0]?.dueDate?.substring(0, 10) ?? "");
                setRegistrationFeeInstallments([{ amount: "", dueDate: "" }]);
            }
        } else {
            setRegistrationFeeEnabled(false);
            setRegistrationFeeAmount("");
            setRegistrationFeeDueDate("");
            setRegistrationFeeInstallments([{ amount: "", dueDate: "" }]);
        }
    }, [showModal, seasonToEdit, division?.sport?.key]);

    useEffect(() => {
        const form = formRef.current;
        if (!form || !seasonToEdit || !useOvertime) return;

        const otWin = form.elements.namedItem("ot_win_points");
        const otLoss = form.elements.namedItem("ot_loss_points");

        if (otWin) otWin.value = seasonToEdit.pointSystem?.otWinPoints ?? 0;
        if (otLoss) otLoss.value = seasonToEdit.pointSystem?.otLossPoints ?? 0;
    }, [useOvertime, seasonToEdit]);

    if (!showModal) return null;

    const seasonLabel =
        seasonToEdit?.name || (division?.name ? `${division.name} ${t("editSeasonModal.fallback.season")}` : t("editSeasonModal.fallback.season"));

    const getFormIntValue = (form, name, fallback = 0) => {
        const value = form.elements.namedItem(name)?.value;
        const parsedValue = parseInt(value, 10);
        return Number.isNaN(parsedValue) ? fallback : parsedValue;
    };

    const validateInstallments = (installments) => {
        if (registrationFeeType !== "installments") return true;
        if (!installments.length) {
            toast.error(t("editSeasonModal.validation.atLeastOneInstallment"), {
                position: "top-center",
            });
            return false;
        }
        for (let i = 0; i < installments.length; i++) {
            const inst = installments[i];
            if (!inst.amount || Number(inst.amount) <= 0) {
                toast.error(
                    t("editSeasonModal.validation.installmentPositiveAmount", {
                        number: i + 1,
                    }),
                    { position: "top-center" }
                );
                return false;
            }
            if (!inst.dueDate) {
                toast.error(
                    t("editSeasonModal.validation.installmentDueDate", {
                        number: i + 1,
                    }),
                    { position: "top-center" }
                );
                return false;
            }
        }
        return true;
    };

    return (
        <>
            <div
                style={ui.overlay}
                role="dialog"
                aria-modal="true"
                aria-labelledby="editSeasonModalTitle"
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget) closeModal();
                }}
            >
                <div style={ui.dialog}>
                    <div id="kbola-edit-season-modal" style={ui.card}>
                        <Form
                            ref={formRef}
                            id="editSeason"
                            method="post"
                            onSubmit={(e) => {
                                e.preventDefault();
                                const form = e.target;
                                const seasonId = parseInt(form.elements.namedItem("id").value, 10);

                                let registrationFee = null;
                                if (registrationFeeEnabled) {
                                    if (registrationFeeType === "installments") {
                                        if (!validateInstallments(registrationFeeInstallments)) return;
                                        registrationFee = {
                                            type: registrationFeeType,
                                            installments: registrationFeeInstallments.map((inst, idx) => ({
                                                amount: parseFloat(inst.amount),
                                                dueDate: inst.dueDate,
                                                installmentNumber: idx + 1,
                                            })),
                                        };
                                    } else {
                                        if (!registrationFeeAmount || !registrationFeeDueDate) {
                                            toast.error(t("editSeasonModal.validation.registrationFeeRequired"), {
                                                autoClose: 3000,
                                                toastId: "registration_fee_required",
                                                position: "top-center",
                                                closeOnClick: true,
                                            });
                                            return;
                                        }
                                        registrationFee = {
                                            type: registrationFeeType,
                                            amount: parseFloat(registrationFeeAmount),
                                            dueDate: registrationFeeDueDate,
                                        };
                                    }
                                }

                                const lateRegistrationFeeInput =
                                    registrationFeeEnabled && lateRegistrationFee !== ""
                                        ? parseFloat(lateRegistrationFee)
                                        : null;

                                editSeason({
                                    variables: {
                                        input: {
                                            seasonId,
                                            name: form.elements.namedItem("name").value,
                                            registrationStartDate: form.elements.namedItem("registration_start_date").value,
                                            registrationEndDate: form.elements.namedItem("registration_end_date").value,
                                            lateRegistrationUntil: form.elements.namedItem("late_registration_until").value,
                                            startDate: form.elements.namedItem("start_date").value,
                                            endDate: form.elements.namedItem("end_date").value,
                                            waiverIds: selectedWaiverIds,
                                            pointSystem: ["baseball", "softball"].includes(division?.sport?.key)
                                                ? null
                                                : {
                                                    winPoints: getFormIntValue(form, "win_points", 1),
                                                    drawPoints: getFormIntValue(form, "draw_points", 0),
                                                    losePoints: getFormIntValue(form, "lose_points", 0),
                                                    useOvertime: form.elements.namedItem("use_overtime").checked,
                                                    otWinPoints: form.elements.namedItem("ot_win_points")?.value
                                                        ? getFormIntValue(form, "ot_win_points", 1)
                                                        : getFormIntValue(form, "win_points", 1),
                                                    otLossPoints: form.elements.namedItem("ot_loss_points")?.value
                                                        ? getFormIntValue(form, "ot_loss_points", 0)
                                                        : getFormIntValue(form, "lose_points", 0),
                                                    forfeitWinPoints: getFormIntValue(form, "forfeit_win_points", 3),
                                                    forfeitLossPoints: getFormIntValue(form, "forfeit_loss_points", 0),
                                                    forfeitWinScore: getFormIntValue(form, "forfeit_win_score", 3),
                                                    forfeitLossScore: getFormIntValue(form, "forfeit_loss_score", 0),
                                                    doubleForfeitPoints: getFormIntValue(form, "double_forfeit_points", 0),
                                                },
                                            registrationFee,
                                            lateRegistrationFee: lateRegistrationFeeInput,
                                        },
                                    },
                                })
                                    .then(() => {
                                        toast.success(t("editSeasonModal.toast.updated"), {
                                            autoClose: 3000,
                                            toastId: "season_updated",
                                            position: "top-center",
                                            closeOnClick: true,
                                        });
                                        setShowModal(false);
                                        cleanupBootstrapModal();
                                        setTimeout(() => window.location.reload(), 3000);
                                    })
                                    .catch((err) => {
                                        toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                                            autoClose: 3000,
                                            toastId: "season_error",
                                            position: "top-center",
                                            closeOnClick: true,
                                        });
                                        cleanupBootstrapModal();
                                    });
                            }}
                        >
                            <div style={ui.header}>
                                <div style={ui.titleWrap}>
                                    <span style={ui.badge}>{t("editSeasonModal.badge")}</span>
                                    <div style={{ minWidth: 0 }}>
                                        <h4 id="editSeasonModalTitle" style={ui.title}>
                                            {t("editSeasonModal.title")}
                                        </h4>
                                        <p style={ui.sub}>
                                            {t("editSeasonModal.updateSettingsFor", { season: seasonLabel })}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={closeModal}
                                    aria-label={t("editSeasonModal.buttons.close")}
                                    title={t("editSeasonModal.buttons.close")}
                                    style={ui.closeBtn}
                                >
                                    <i className="fas fa-times" />
                                </button>
                            </div>

                            <div style={ui.body}>
                                <input type="hidden" name="id" />

                                <div style={ui.section}>
                                    <div style={ui.sectionTitleRow}>
                                        <p style={ui.sectionTitle}>{t("editSeasonModal.sections.season")}</p>
                                        <span style={ui.hint}>{t("editSeasonModal.sectionHints.season")}</span>
                                    </div>

                                    <div className="row">
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label style={ui.label}>{t("editSeasonModal.fields.name")}</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    required
                                                    name="name"
                                                    placeholder={t("editSeasonModal.placeholders.seasonName")}
                                                    style={ui.input}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label style={ui.label}>{t("editSeasonModal.fields.startDate")}</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="start_date"
                                                    style={ui.input}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label style={ui.label}>{t("editSeasonModal.fields.endDate")}</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="end_date"
                                                    style={ui.input}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label style={ui.label}>{t("seasonList.headers.registrationStart")}</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="registration_start_date"
                                                    style={ui.input}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label style={ui.label}>{t("seasonList.headers.registrationEnd")}</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="registration_end_date"
                                                    style={ui.input}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-4">
                                            <div className="form-group">
                                                <label style={ui.label}>{t("editSeasonModal.fields.lateRegistrationUntil")}</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    name="late_registration_until"
                                                    style={ui.input}
                                                />
                                            </div>
                                        </div>
                                    </div>



                                    <div style={ui.divider} />

                                    <div className="form-group mb-0">
                                        <label style={ui.label}>{t("editSeasonModal.fields.waiverOptional")}</label>
                                        <div className="kbola-waiver-picker-wrap">
                                            <select
                                                className="form-control kbola-waiver-picker"
                                                name="waiverIds"
                                                style={ui.select}
                                                value={selectedWaiverIds}
                                                multiple
                                                onChange={(e) =>
                                                    setSelectedWaiverIds(
                                                        Array.from(e.target.selectedOptions).map((opt) => opt.value)
                                                    )
                                                }
                                                size={Math.min(6, Math.max(3, waivers?.length || 0))}
                                            >
                                                {waivers?.map((waiver) => (
                                                    <option key={waiver.id} value={waiver.id}>
                                                        {waiver.title}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {selectedWaiverTitles.length > 0 && (
                                            <div className="kbola-waiver-chip-row">
                                                {selectedWaiverTitles.map((title, index) => (
                                                    <span className="kbola-waiver-chip" key={`${title}-${index}`}>
                                                        {title}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {!["baseball", "softball"].includes(division?.sport?.key) && (
                                    <div style={ui.section}>
                                        <div style={ui.sectionTitleRow}>
                                            <p style={ui.sectionTitle}>{t("editSeasonModal.sections.pointSystem")}</p>
                                            <span style={ui.hint}>{t("editSeasonModal.sectionHints.pointSystem")}</span>
                                        </div>

                                            <div className="row">
                                                <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label style={ui.label}>{t("editSeasonModal.fields.winPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="win_points"
                                                        required
                                                        style={ui.input}
                                                    />
                                                </div>
                                            </div>

                                                <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label style={ui.label}>{t("editSeasonModal.fields.drawPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="draw_points"
                                                        required
                                                        style={ui.input}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label style={ui.label}>{t("editSeasonModal.fields.losePoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="lose_points"
                                                        required
                                                        style={ui.input}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div style={{ ...ui.hint, marginBottom: 10 }}>
                                            {t("editSeasonModal.sectionHints.forfeit")}
                                        </div>
                                        <div className="row">
                                            <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label style={ui.label}>{t("editSeasonModal.fields.forfeitWinPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="forfeit_win_points"
                                                        required
                                                        style={ui.input}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label style={ui.label}>{t("editSeasonModal.fields.forfeitLossPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="forfeit_loss_points"
                                                        required
                                                        style={ui.input}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label style={ui.label}>{t("editSeasonModal.fields.doubleForfeitPoints")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="double_forfeit_points"
                                                        required
                                                        style={ui.input}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label style={ui.label}>{t("editSeasonModal.fields.forfeitWinScore")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="forfeit_win_score"
                                                        required
                                                        style={ui.input}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-sm-4">
                                                <div className="form-group">
                                                    <label style={ui.label}>{t("editSeasonModal.fields.forfeitLossScore")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="forfeit_loss_score"
                                                        required
                                                        style={ui.input}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div style={ui.inlineRow}>
                                            <label className="mb-0" style={{ ...ui.chip, cursor: "pointer" }}>
                                                <input
                                                    type="checkbox"
                                                    className="mr-2"
                                                    name="use_overtime"
                                                    id="useOvertime"
                                                    onChange={(e) => setUseOvertime(e.target.checked)}
                                                    checked={useOvertime}
                                                    style={{ transform: "translateY(1px)" }}
                                                />
                                                {t("editSeasonModal.toggles.useOvertime")}
                                            </label>
                                            <span style={ui.hint}>{t("editSeasonModal.sectionHints.overtime")}</span>
                                        </div>

                                        {useOvertime && (
                                            <>
                                                <div style={ui.divider} />
                                                <div className="row">
                                                    <div className="col-sm-4">
                                                        <div className="form-group">
                                                            <label style={ui.label}>{t("editSeasonModal.fields.otWinPoints")}</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                name="ot_win_points"
                                                                placeholder={t("editSeasonModal.placeholders.otWinPoints")}
                                                                style={ui.input}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-sm-4">
                                                        <div className="form-group">
                                                            <label style={ui.label}>{t("editSeasonModal.fields.otLossPoints")}</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                name="ot_loss_points"
                                                                placeholder={t("editSeasonModal.placeholders.otLossPoints")}
                                                                style={ui.input}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div style={ui.section}>
                                    <div style={ui.sectionTitleRow}>
                                        <p style={ui.sectionTitle}>{t("editSeasonModal.sections.registrationFee")}</p>
                                        <span style={ui.hint}>{t("editSeasonModal.sectionHints.registrationFee")}</span>
                                    </div>

                                    <div style={ui.inlineRow}>
                                        <label className="mb-0" style={{ ...ui.chip, cursor: "pointer" }}>
                                            <input
                                                type="checkbox"
                                                className="mr-2"
                                                id="registrationFeeEnabled"
                                                checked={registrationFeeEnabled}
                                                onChange={() => setRegistrationFeeEnabled(!registrationFeeEnabled)}
                                                style={{ transform: "translateY(1px)" }}
                                            />
                                            {t("editSeasonModal.toggles.enableRegistrationFee")}
                                        </label>
                                    </div>

                                    {registrationFeeEnabled && (
                                        <>
                                            <div style={ui.divider} />

                                            <div className="row">
                                                <div className="col-sm-6 col-md-4">
                                                    <div className="form-group">
                                                        <label style={ui.label}>{t("editSeasonModal.fields.feeType")}</label>
                                                        <select
                                                            className="form-control"
                                                            value={registrationFeeType}
                                                            onChange={(e) => setRegistrationFeeType(e.target.value)}
                                                            style={ui.select}
                                                        >
                                                            <option value="none">{t("editSeasonModal.options.none")}</option>
                                                            <option value="mandatory_on_registration">
                                                                {t("editSeasonModal.options.mandatoryOnRegistration")}
                                                            </option>
                                                            <option value="pay_later">
                                                                {t("editSeasonModal.options.payLater")}
                                                            </option>
                                                            <option value="installments">
                                                                {t("editSeasonModal.options.installments")}
                                                            </option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {registrationFeeType !== "installments" && (
                                                <div className="row">
                                                    <div className="col-sm-4">
                                                        <div className="form-group">
                                                            <label style={ui.label}>{t("editSeasonModal.fields.amount")}</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                min={0}
                                                                step="0.01"
                                                                value={registrationFeeAmount}
                                                                onChange={(e) => setRegistrationFeeAmount(e.target.value)}
                                                                placeholder={t("editSeasonModal.placeholders.amountExample")}
                                                                style={ui.input}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-sm-4">
                                                        <div className="form-group">
                                                            <label style={ui.label}>{t("editSeasonModal.fields.dueDate")}</label>
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                value={registrationFeeDueDate}
                                                                onChange={(e) => setRegistrationFeeDueDate(e.target.value)}
                                                                style={ui.input}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {registrationFeeType === "installments" && (
                                                <div>
                                                    <div style={ui.inlineRow}>
                                                        <span style={{ ...ui.chip, background: "rgba(0,0,0,0.02)" }}>
                                                            <i className="fas fa-layer-group" /> {t("editSeasonModal.installmentChip")}
                                                        </span>
                                                        <span style={ui.hint}>{t("editSeasonModal.sectionHints.installments")}</span>
                                                    </div>

                                                    <div style={{ height: 10 }} />

                                                    {registrationFeeInstallments.map((installment, idx) => (
                                                        <div className="row align-items-end mb-2" key={idx}>
                                                            <div className="col-4">
                                                                <input
                                                                    type="number"
                                                                    className="form-control"
                                                                    min={0}
                                                                    step="0.01"
                                                                    placeholder={t("editSeasonModal.placeholders.installmentAmount", {
                                                                        number: idx + 1,
                                                                    })}
                                                                    value={installment.amount}
                                                                    onChange={(e) => {
                                                                        const updated = [...registrationFeeInstallments];
                                                                        updated[idx].amount = e.target.value;
                                                                        setRegistrationFeeInstallments(updated);
                                                                    }}
                                                                    style={ui.input}
                                                                />
                                                            </div>

                                                            <div className="col-4">
                                                                <input
                                                                    type="date"
                                                                    className="form-control"
                                                                    value={installment.dueDate}
                                                                    onChange={(e) => {
                                                                        const updated = [...registrationFeeInstallments];
                                                                        updated[idx].dueDate = e.target.value;
                                                                        setRegistrationFeeInstallments(updated);
                                                                    }}
                                                                    style={ui.input}
                                                                />
                                                            </div>

                                                            <div className="col-2">
                                                                {registrationFeeInstallments.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        style={{ ...ui.btn, ...ui.btnDanger, padding: "8px 10px" }}
                                                                        onClick={() =>
                                                                            setRegistrationFeeInstallments(
                                                                                registrationFeeInstallments.filter((_, i) => i !== idx)
                                                                            )
                                                                        }
                                                                        title={t("editSeasonModal.titles.removeInstallment")}
                                                                    >
                                                                        <i className="fas fa-minus" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <button
                                                        type="button"
                                                        style={{ ...ui.btn, padding: "10px 12px" }}
                                                        onClick={() =>
                                                            setRegistrationFeeInstallments([
                                                                ...registrationFeeInstallments,
                                                                { amount: "", dueDate: "" },
                                                            ])
                                                        }
                                                    >
                                                        <i className="fas fa-plus" />
                                                        {t("editSeasonModal.buttons.addInstallment")}
                                                    </button>
                                                </div>
                                            )}

                                            <Divider style={{ margin: "20px 0" }} />

                                            <div className="col-sm-6 col-md-4">
                                                <div className="form-group">
                                                    <label style={ui.labelFlagged}>{t("editSeasonModal.fields.lateRegistrationFee")}</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        min={0}
                                                        step="0.01"
                                                        value={lateRegistrationFee}
                                                        onChange={(e) => setLateRegistrationFee(e.target.value)}
                                                        placeholder={t("editSeasonModal.placeholders.amountExample")}
                                                        style={ui.input}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div style={ui.footer}>
                                <button type="button" style={ui.btn} onClick={closeModal}>
                                    <i className="fas fa-times" />
                                    {t("editSeasonModal.buttons.close")}
                                </button>

                                <button type="submit" style={{ ...ui.btn, ...ui.btnPrimary }}>
                                    <i className="fas fa-check" />
                                    {t("editSeasonModal.buttons.saveChanges")}
                                </button>
                            </div>

                            <style>{`
  #kbola-edit-season-modal {
    font-family: 'Poppins', sans-serif;
  }

  #kbola-edit-season-modal input,
  #kbola-edit-season-modal select,
  #kbola-edit-season-modal textarea,
  #kbola-edit-season-modal button,
  #kbola-edit-season-modal label,
  #kbola-edit-season-modal p,
  #kbola-edit-season-modal h1,
  #kbola-edit-season-modal h2,
  #kbola-edit-season-modal h3,
  #kbola-edit-season-modal h4,
  #kbola-edit-season-modal h5,
  #kbola-edit-season-modal h6,
  #kbola-edit-season-modal small,
  #kbola-edit-season-modal span {
    font-family: inherit;
  }

  #kbola-edit-season-modal i.fas,
  #kbola-edit-season-modal i.far,
  #kbola-edit-season-modal i.fal,
  #kbola-edit-season-modal i.fa,
  #kbola-edit-season-modal i[class^="fa-"],
  #kbola-edit-season-modal i[class*=" fa-"] {
    font-family: "Font Awesome 5 Free" !important;
    font-weight: 900;
  }

  #kbola-edit-season-modal i.fab {
    font-family: "Font Awesome 5 Brands" !important;
    font-weight: 400;
  }

  #kbola-edit-season-modal .form-control:focus,
  #kbola-edit-season-modal button:focus,
  #kbola-edit-season-modal select:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
    border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
  }

  #kbola-edit-season-modal button i {
    line-height: 1;
  }

  #kbola-edit-season-modal .kbola-waiver-picker-wrap {
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.11);
    background: linear-gradient(180deg, rgba(255,255,255,0.94), rgba(247,248,250,0.92));
    padding: 8px;
  }

  #kbola-edit-season-modal .kbola-waiver-picker {
    min-height: 132px;
    height: auto !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
  }

  #kbola-edit-season-modal .kbola-waiver-picker option {
    border-radius: 9px;
    margin: 2px 0;
    padding: 9px 10px;
    font-weight: 700;
  }

  #kbola-edit-season-modal .kbola-waiver-picker option:checked {
    background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.72);
    color: #14151a;
  }

  #kbola-edit-season-modal .kbola-waiver-picker-wrap:focus-within {
    border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.95);
    box-shadow: 0 0 0 0.22rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
  }

  #kbola-edit-season-modal .kbola-waiver-chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }

  #kbola-edit-season-modal .kbola-waiver-chip {
    font-size: 11px;
    font-weight: 800;
    border-radius: 999px;
    padding: 5px 9px;
    color: rgba(20, 21, 26, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.42);
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  #kbola-edit-season-modal .kbola-waiver-picker::-webkit-scrollbar {
    width: 10px;
  }

  #kbola-edit-season-modal .kbola-waiver-picker::-webkit-scrollbar-thumb {
    border-radius: 999px;
    border: 2px solid rgba(255,255,255,0.95);
    background: rgba(20, 21, 26, 0.24);
  }
`}</style>
                        </Form>
                    </div>
                </div>
            </div>
        </>
    );
}
